import React from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  AlertCircle,
  ArrowLeft,
  Shield,
  RefreshCw,
  Home,
  HelpCircle,
} from "lucide-react";

const PaymentFailed = () => {
  const navigate = useNavigate();

  const handleRetry = () => {
    navigate("/payment");
  };

  const handleGoHome = () => {
    navigate("/home");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-orange-50 flex flex-col items-center justify-center p-4">
      {/* Branding Header */}
      <div className="absolute top-6 left-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 blur opacity-70 animate-pulse"></div>
            <img
              src="/QemplateFull.png"
              alt="Qemplate Logo"
              className="relative h-10 w-auto z-10"
            />
          </div>
        </div>
      </div>

      {/* Error Card */}
      <div className="max-w-2xl w-full">
        {/* Main Error Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-6">
          {/* Header */}
          <div className="bg-gradient-to-br from-red-500 via-orange-500 to-pink-500 p-8 text-white relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            ></div>
            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full mb-6">
                <X className="h-12 w-12 text-white" strokeWidth={3} />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-3">
                Payment Cancelled
              </h1>
              <p className="text-lg text-red-100">
                Your payment was not completed.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Status Message */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-green-100 rounded-full p-3">
                  <Shield className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                No charges were made
              </h2>
              <p className="text-gray-600">
                You canceled the checkout process. Your account remains on the
                free plan.
              </p>
            </div>

            {/* Info Box */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 mb-6 border border-red-200">
              <div className="flex items-start gap-3">
                <div className="bg-red-100 rounded-lg p-2 flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    What happened?
                  </h3>
                  <p className="text-sm text-gray-700">
                    You chose to cancel the payment process. If you encountered
                    any issues during checkout, please try again or contact our
                    support team for assistance.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white rounded-xl hover:shadow-2xl hover:shadow-purple-500/50 transform hover:-translate-y-0.5 transition-all duration-200 font-semibold"
              >
                <RefreshCw size={18} />
                <span>Retry checkout</span>
                <ArrowLeft size={18} className="rotate-180" />
              </button>

              <button
                onClick={handleGoHome}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
              >
                <Home size={18} />
                <span>Go to Home</span>
              </button>
            </div>
          </div>
        </div>

        {/* Help Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-indigo-600" />
            Need Help?
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            If you're experiencing issues with checkout, try these steps:
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="bg-indigo-100 rounded-lg p-1.5 flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Check your internet connection
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Ensure you have a stable connection
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-indigo-100 rounded-lg p-1.5 flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Verify your card details
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Double-check card number, expiry, and CVV
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="bg-indigo-100 rounded-lg p-1.5 flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Contact support
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Our team is here to help you
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailed;
